import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Edit, Eye, Trash2, Printer, Filter } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { TransferEditDialog } from '../../components/admin/TransferEditDialog';
import { TransferViewDialog } from '../../components/admin/TransferViewDialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTransfersPage() {
  const { t } = useLanguage();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [viewingTransfer, setViewingTransfer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [formData, setFormData] = useState({ status: '', payment_status: '', settlement_id: '', settled_amount: '', completed_at: '', proof_image: '' });
  const [proofPreview, setProofPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewImage, setViewImage] = useState(null);

  useEffect(() => { fetchTransfers(); }, []);

  useEffect(() => {
    if (!transfers.length) { setFiltered([]); return; }
    let result = [...transfers];
    if (dateFrom) result = result.filter(t => t.created_at >= dateFrom);
    if (dateTo) result = result.filter(t => t.created_at <= dateTo + 'T23:59:59');
    setFiltered(result);
  }, [transfers, dateFrom, dateTo]);

  const fetchTransfers = async () => {
    try {
      const response = await api.getAdminTransfers();
      setTransfers(response.data);
    } catch (error) { console.error('Failed to fetch transfers:', error); }
    finally { setLoading(false); }
  };

  const toLocalDate = (isoStr) => {
    if (!isoStr) return '';
    try { return new Date(isoStr).toISOString().slice(0, 10); } catch { return ''; }
  };

  const openEditDialog = (transfer) => {
    setEditingTransfer(transfer);
    setFormData({
      status: transfer.status, payment_status: transfer.payment_status,
      settlement_id: transfer.settlement_id || '', settled_amount: transfer.settled_amount || '',
      completed_at: toLocalDate(transfer.completed_at), proof_image: transfer.proof_image || '',
    });
    setProofPreview(transfer.proof_image ? t('admin_transfers_pdf_uploaded') : null);
    setDialogOpen(true);
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error(t('admin_transfers_pdf_only')); return; }
    setProofPreview(file.name); setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/transfers/upload-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const { file_url, extracted } = res.data;
      const updates = { proof_image: file_url };
      if (extracted) {
        if (extracted.settlement_id) updates.settlement_id = extracted.settlement_id;
        if (extracted.settled_amount) updates.settled_amount = String(extracted.settled_amount);
      }
      setFormData(prev => ({ ...prev, ...updates }));
      const filled = [extracted?.settlement_id && t('print_settlement_id'), extracted?.settled_amount && t('print_settled_amount')].filter(Boolean);
      toast.success(filled.length > 0 ? t('print_pdf_read').replace('{fields}', filled.join('・')) : t('admin_transfers_pdf_uploaded'));
    } catch { toast.error(t('admin_transfers_upload_failed')); setProofPreview(null); }
    finally { setUploading(false); }
  };

  const handleUpdate = async () => {
    if (formData.status === 'completed' && !formData.completed_at) { toast.error(t('admin_transfers_completed_required')); return; }
    try {
      const payload = { status: formData.status, payment_status: formData.payment_status };
      if (formData.status === 'completed') {
        payload.settlement_id = formData.settlement_id || null;
        payload.settled_amount = formData.settled_amount ? parseFloat(formData.settled_amount) : null;
        payload.completed_at = formData.completed_at ? new Date(formData.completed_at).toISOString() : null;
        payload.proof_image = formData.proof_image || null;
      }
      await api.updateTransfer(editingTransfer.id, payload);
      toast.success(t('admin_transfers_updated')); setDialogOpen(false); fetchTransfers();
    } catch { toast.error(t('admin_transfers_update_failed')); }
  };

  const handleDelete = async (transferId) => {
    if (!window.confirm(t('admin_transfers_delete_confirm'))) return;
    try {
      await api.deleteTransfer(transferId);
      toast.success(t('admin_transfers_deleted')); fetchTransfers();
    } catch { toast.error(t('admin_transfers_delete_failed')); }
  };

  const clearFilter = () => { setDateFrom(''); setDateTo(''); };

  const handlePrint = () => {
    const win = window.open('', '_blank');
    const statusLabel = (s) => ({ pending: t('status_pending'), processing: t('status_processing'), completed: t('status_completed'), failed: t('status_failed'), cancelled: t('status_cancelled') }[s] || s);
    const periodText = dateFrom || dateTo ? `${t('print_period')}: ${dateFrom || t('print_no_start')} ~ ${dateTo || t('print_no_end')}` : t('print_period_all');
    const totalJPY = filtered.reduce((s, t) => s + (t.total_jpy || 0), 0);
    const totalBRL = filtered.reduce((s, t) => s + (t.amount_brl || 0), 0);
    const totalFee = filtered.reduce((s, t) => s + (t.fee_jpy || 0), 0);

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t('print_title_admin')}</title>
<style>* { margin: 0; padding: 0; box-sizing: border-box; } body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 24px; color: #1a1a1a; font-size: 12px; } .header { text-align: center; border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 20px; } .header h1 { font-size: 20px; } .period { text-align: center; font-size: 11px; color: #666; margin-bottom: 16px; } table { width: 100%; border-collapse: collapse; margin-bottom: 20px; } th { background: #f5f5f5; border: 1px solid #ddd; padding: 8px 5px; font-size: 9px; } td { border: 1px solid #ddd; padding: 6px 5px; font-size: 10px; } .num { text-align: right; font-family: monospace; } .footer { text-align: center; font-size: 10px; color: #999; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 12px; } .summary { display: flex; justify-content: space-between; margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 6px; } .summary-item { text-align: center; } .summary-label { font-size: 10px; color: #666; } .summary-value { font-size: 16px; font-weight: 700; margin-top: 2px; } @media print { body { padding: 12px; } }</style></head><body>
<div class="header"><h1>${t('print_title_admin')}</h1><p style="font-size:11px;color:#666;margin-top:4px">${t('print_date')}: ${new Date().toLocaleDateString('ja-JP')}</p></div>
<div class="period">${periodText} (${filtered.length}${t('print_items')})</div>
<div class="summary"><div class="summary-item"><div class="summary-label">${t('print_total_jpy')}</div><div class="summary-value">¥${totalJPY.toLocaleString()}</div></div><div class="summary-item"><div class="summary-label">${t('print_total_brl')}</div><div class="summary-value">R$ ${totalBRL.toLocaleString()}</div></div><div class="summary-item"><div class="summary-label">${t('print_total_fee')}</div><div class="summary-value">¥${totalFee.toLocaleString()}</div></div></div>
<table><thead><tr><th>${t('print_col_no')}</th><th>${t('print_col_date')}</th><th>${t('print_col_sender')}</th><th>${t('print_col_recipient')}</th><th>${t('print_col_amount_jpy')}</th><th>${t('print_col_fee')}</th><th>${t('print_col_total_jpy')}</th><th>${t('print_col_amount_brl')}</th><th>${t('print_col_rate')}</th><th>${t('print_col_status')}</th><th>${t('print_col_payment')}</th><th>${t('print_col_id')}</th></tr></thead><tbody>`);

    filtered.forEach((tr, i) => {
      const payLabel = { awaiting: t('print_payment_unpaid'), paid: t('print_payment_paid'), confirmed: t('print_payment_confirmed') }[tr.payment_status] || tr.payment_status || '-';
      win.document.write(`<tr><td>${i + 1}</td><td>${new Date(tr.created_at).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td><td>${tr.sender_name || '-'}</td><td>${tr.recipient?.full_name || '-'}</td><td class="num">¥${(tr.amount_jpy || 0).toLocaleString()}</td><td class="num">¥${(tr.fee_jpy || 0).toLocaleString()}</td><td class="num">¥${(tr.total_jpy || 0).toLocaleString()}</td><td class="num">R$ ${(tr.amount_brl || 0).toLocaleString()}</td><td class="num">${(tr.exchange_rate || 0).toFixed(5)}</td><td><span class="status status-${tr.status}">${statusLabel(tr.status)}</span></td><td>${payLabel}</td><td style="font-family:monospace;font-size:8px;">${tr.id.substring(0, 13)}</td></tr>`);
    });

    win.document.write(`</tbody></table><div class="footer">PRC Remit ${t('print_admin_label')} | prcremit.com | ${t('print_footer_admin')}</div></body></html>`);
    win.document.close(); win.focus(); setTimeout(() => win.print(), 500);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-transfers-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary mb-1">{t('admin_transfers_title')}</h1>
            <p className="text-muted-foreground text-sm">{t('admin_transfers_subtitle')}</p>
          </div>
          {filtered.length > 0 && (
            <Button variant="outline" onClick={handlePrint} className="flex-shrink-0" data-testid="print-transfers-button">
              <Printer className="h-4 w-4 mr-2" />{t('print')}
            </Button>
          )}
        </div>

        {/* Date Filter */}
        <div className="bg-card border border-border/50 rounded-2xl p-4 mb-6" data-testid="admin-date-filter">
          <div className="flex flex-col sm:flex-row items-end gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
              <Filter className="h-4 w-4" />{t('admin_transfers_period_filter')}
            </div>
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t('date_start')}</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="admin-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('date_end')}</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="admin-date-to" />
              </div>
            </div>
            {(dateFrom || dateTo) && (
              <Button variant="ghost" size="sm" onClick={clearFilter} className="text-xs flex-shrink-0" data-testid="admin-clear-filter">{t('clear_filter')}</Button>
            )}
          </div>
          {(dateFrom || dateTo) && <div className="text-xs text-muted-foreground mt-2">{filtered.length}{t('admin_transfers_count_records')}</div>}
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">ID</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_transfers_sender')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_transfers_recipient')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_transfers_amount_jpy')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_transfers_amount_brl')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('admin_users_status')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('history_date')}</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-muted-foreground">{t('edit')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((transfer) => (
                    <tr key={transfer.id} className="hover:bg-secondary/30 smooth-transition" data-testid={`transfer-row-${transfer.id}`}>
                      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{transfer.id.substring(0, 8)}</td>
                      <td className="px-6 py-4"><div className="text-sm font-medium text-primary">{transfer.sender_name || '-'}</div></td>
                      <td className="px-6 py-4"><div className="text-sm font-medium">{transfer.recipient?.full_name || '-'}</div></td>
                      <td className="px-6 py-4 font-mono font-medium text-primary">¥{transfer.total_jpy.toLocaleString()}</td>
                      <td className="px-6 py-4 font-mono font-medium text-accent">R$ {transfer.amount_brl.toLocaleString()}</td>
                      <td className="px-6 py-4"><StatusBadge status={transfer.status} type="transfer" /></td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">{new Date(transfer.created_at).toLocaleString('ja-JP', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => { setViewingTransfer(transfer); setViewDialogOpen(true); }} data-testid={`view-transfer-${transfer.id}`}><Eye className="h-4 w-4 mr-2" />{t('admin_transfers_detail')}</Button>
                          <Button size="sm" variant="outline" onClick={() => openEditDialog(transfer)} data-testid={`edit-transfer-${transfer.id}`}><Edit className="h-4 w-4 mr-2" />{t('edit')}</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(transfer.id)} data-testid={`delete-transfer-${transfer.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <TransferEditDialog
          open={dialogOpen} onOpenChange={setDialogOpen} transfer={editingTransfer}
          formData={formData} setFormData={setFormData}
          proofPreview={proofPreview} setProofPreview={setProofPreview}
          uploading={uploading} onProofUpload={handleProofUpload} onSubmit={handleUpdate}
        />

        <TransferViewDialog
          open={viewDialogOpen} onOpenChange={setViewDialogOpen} transfer={viewingTransfer}
          onEdit={() => { setViewDialogOpen(false); openEditDialog(viewingTransfer); }}
        />

        {/* PDF Viewer */}
        <Dialog open={!!viewImage} onOpenChange={() => setViewImage(null)}>
          <DialogContent className="max-w-3xl h-[80vh]">
            <DialogHeader><DialogTitle>{t('admin_transfers_proof')}</DialogTitle></DialogHeader>
            {viewImage && <iframe src={viewImage} className="w-full flex-1 rounded-lg mt-2" style={{height: 'calc(80vh - 80px)'}} title={t('admin_transfers_proof_pdf')} data-testid="fullscreen-proof-pdf" />}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
